import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  FileTemplateForm,
  FileTemplateFormValues,
} from 'forms/FileTemplateForm/FileTemplateForm'
import { useDeleteCommand, useEditCommand } from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import { TFunction } from 'i18next'
import { pick } from 'lodash'
import {
  Blob,
  FileTemplate,
  FileTemplateType,
  FileTemplateUpdateInput,
  UserRole,
} from 'model'
import { PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FileTemplateList } from './AdminFileTemplatesList'
import {
  useCreateFileTemplateMutation,
  useDeleteFileTemplatesMutation,
  useReadFileTemplatesQuery,
  useUpdateFileTemplateMutation,
} from './AdminFileTemplatesView.generated'

const AdminFileTemplatesListView: React.FC = () => {
  const { t } = useTranslation()
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const { loading, data, error, refetch } = useReadFileTemplatesQuery()
  const [createFileTemplate] = useCreateFileTemplateMutation()
  const [updateFileTemplate] = useUpdateFileTemplateMutation()
  const [deleteFileTemplates] = useDeleteFileTemplatesMutation()
  const [dialogState, showDialog, hideDialog] = useDialogState()
  const [selection, selectedItems] = useSelection<FileTemplate>({
    getKey: (item) => item._id,
  })
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [uploadedFileName, setUploadedFileName] = React.useState<string>()

  const fileTemplates: FileTemplate[] =
    !loading && data && data.fileTemplates
      ? (data.fileTemplates as FileTemplate[])
      : []

  const handleItemEdit = () => {
    const item = selectedItems[0]
    setUploadedFileName(item.fileName)
    setSelectedItemId(item._id)
    showPanel({
      ...selectedItems[0],
      cellMapping: selectedItems[0].cellMapping || '',
      provingGround: item.provingGround ? item.provingGround._id : '',
    } as FileTemplateUpdateInput)
  }

  useEditCommand(
    { name: 'fileTemplate', count: selectedItems.length },
    {
      priority: 20,
      onClick: handleItemEdit,
    },
    [selectedItems]
  )
  useDeleteCommand(
    { name: 'fileTemplate', count: selectedItems.length },
    {
      priority: 30,
      onClick: showDialog,
    },
    [selectedItems]
  )

  const handleSubmit = React.useCallback(
    async (
      values: FileTemplateFormValues,
      formikActions: FormikActions<any>
    ) => {
      formikActions.setSubmitting(true)

      if (!values.provingGround) {
        delete values.provingGround
      }

      await updateFileTemplate({
        variables: {
          id: selectedItemId,
          update: pick(values, ['type', 'cellMapping', 'provingGround']),
        },
      })

      formikActions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hidePanel()
    },
    [updateFileTemplate, selection, refetch, hidePanel, selectedItemId]
  )

  const handleUploadCompleted = async (files: Partial<Blob>[]) => {
    const blob = files[0]
    const fileTemplate = {
      uri: blob.uri!,
      type: FileTemplateType.NotAssigned,
      fileName: blob.name!,
    }
    const res = await createFileTemplate({
      variables: {
        input: fileTemplate,
      },
    })
    const createdId =
      res.data && res.data.createFileTemplate
        ? res.data.createFileTemplate._id
        : ''
    setSelectedItemId(createdId)
    setUploadedFileName(fileTemplate.fileName)
    refetch()
    const templateType = getTemplateTypeByName(fileTemplate.fileName, t)
    if (templateType) {
      showPanel({
        ...fileTemplate,
        cellMapping: '',
        type: templateType,
      } as FileTemplate)
    } else {
      showPanel(fileTemplate)
    }
  }

  const handleConfirmDelete = React.useCallback(async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((ft) => ft._id)
      await deleteFileTemplates({
        variables: { ids },
      })
    }
    hideDialog()
    refetch()
  }, [selectedItems, deleteFileTemplates, refetch, hideDialog])

  return (
    <FlexSpinner loading={loading} error={error}>
      <FileUpload
        parentId={'fileTemplates'}
        parentType={'fileTemplate'}
        onUploadCompleted={handleUploadCompleted}
        singleFile={true}
        text={t('model.fileTemplate.fileUpload')}
      />
      <FileTemplateList
        items={fileTemplates}
        selection={selection}
        onItemInvoked={handleItemEdit}
      />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        headerText={`${t('model.fileTemplate.edit')} - ${uploadedFileName}`}
        type={PanelType.largeFixed}
      >
        <FileTemplateForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminFileTemplatesView: React.FC = () => (
  <ViewAuthorizer roles={[UserRole.SystemAdministrator]}>
    <StickyPane>
      <AppCommandBar />
      <AdminFileTemplatesListView />
    </StickyPane>
  </ViewAuthorizer>
)

function getTemplateTypeByName(
  fileName: string,
  t: TFunction
): FileTemplateType | null {
  const templateTypes: FileTemplateType[] = Object.keys(FileTemplateType).map(
    (k) => FileTemplateType[k]
  )
  let templateType: FileTemplateType | null = null
  templateTypes.forEach((type: FileTemplateType) => {
    const name = fileName.toLowerCase()
    const translatedType = t(`enum.fileTemplateType.${type}`).toLowerCase()
    if (name.includes(translatedType)) {
      templateType = type
    }
  })
  return templateType
}
